import React, { useState } from 'react';
import { Mail, Phone, MapPin, Send } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { createSupportTicket } from '../utils/api';
import { toast } from 'sonner';

export const Contact = () => {
  const [formData, setFormData] = useState({ name: '', email: '', subject: '', message: '' });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await createSupportTicket(formData);
      toast.success('Message sent successfully! We will get back to you soon.');
      setFormData({ name: '', email: '', subject: '', message: '' });
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Failed to send message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen">
      <section className="bg-gradient-to-br from-blue-700 via-indigo-800 to-purple-900 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <Mail className="h-20 w-20 mx-auto mb-6 text-blue-200" />
          <h1 className="text-5xl md:text-6xl font-extrabold mb-6" style={{fontFamily: 'Work Sans'}}>Get In Touch</h1>
          <p className="text-xl md:text-2xl text-blue-100 max-w-3xl mx-auto">We're here to answer your questions and help you on your journey to excellence</p>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid md:grid-cols-2 gap-12">
          <div className="space-y-8">
            <div>
              <h2 className="text-3xl font-bold mb-6">Contact Information</h2>
              <p className="text-lg text-gray-600 mb-8">Reach out to us through any of these channels</p>
            </div>

            <Card className="hover:shadow-xl transition-shadow border-l-4 border-l-blue-600">
              <CardContent className="p-8">
                <div className="flex items-start gap-6">
                  <div className="bg-blue-100 p-4 rounded-full">
                    <Mail className="h-8 w-8 text-blue-700" />
                  </div>
                  <div>
                    <p className="font-bold text-xl mb-2">Email</p>
                    <p className="text-gray-700">info@nigerlandconsult.com</p>
                    <p className="text-sm text-gray-500 mt-2">We'll respond within 24 hours</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="hover:shadow-xl transition-shadow border-l-4 border-l-green-600">
              <CardContent className="p-8">
                <div className="flex items-start gap-6">
                  <div className="bg-green-100 p-4 rounded-full">
                    <Phone className="h-8 w-8 text-green-700" />
                  </div>
                  <div>
                    <p className="font-bold text-xl mb-2">Phone</p>
                    <p className="text-gray-700">+234 XXX XXX XXXX</p>
                    <p className="text-sm text-gray-500 mt-2">Mon-Fri, 9AM-5PM WAT</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="hover:shadow-xl transition-shadow border-l-4 border-l-purple-600">
              <CardContent className="p-8">
                <div className="flex items-start gap-6">
                  <div className="bg-purple-100 p-4 rounded-full">
                    <MapPin className="h-8 w-8 text-purple-700" />
                  </div>
                  <div>
                    <p className="font-bold text-xl mb-2">Location</p>
                    <p className="text-gray-700">Nigeria</p>
                    <p className="text-sm text-gray-500 mt-2">Serving clients nationwide</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <Card className="shadow-2xl">
            <CardHeader className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-t-2xl">
              <CardTitle className="text-2xl flex items-center gap-3">
                <Send className="h-7 w-7" />
                Send Us a Message
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-8">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <Label htmlFor="name" className="text-base">Full Name *</Label>
                  <Input 
                    id="name" 
                    required 
                    className="mt-2 h-12" 
                    placeholder="John Doe"
                    value={formData.name} 
                    onChange={(e) => setFormData({...formData, name: e.target.value})} 
                  />
                </div>
                <div>
                  <Label htmlFor="email" className="text-base">Email Address *</Label>
                  <Input 
                    id="email" 
                    type="email" 
                    required 
                    className="mt-2 h-12" 
                    placeholder="john@example.com"
                    value={formData.email} 
                    onChange={(e) => setFormData({...formData, email: e.target.value})} 
                  />
                </div>
                <div>
                  <Label htmlFor="subject" className="text-base">Subject *</Label>
                  <Input 
                    id="subject" 
                    required 
                    className="mt-2 h-12" 
                    placeholder="How can we help you?"
                    value={formData.subject} 
                    onChange={(e) => setFormData({...formData, subject: e.target.value})} 
                  />
                </div>
                <div>
                  <Label htmlFor="message" className="text-base">Message *</Label>
                  <Textarea 
                    id="message" 
                    required 
                    rows={6} 
                    className="mt-2" 
                    placeholder="Tell us more about your inquiry..."
                    value={formData.message} 
                    onChange={(e) => setFormData({...formData, message: e.target.value})} 
                  />
                </div>
                <Button 
                  type="submit" 
                  className="w-full h-14 text-lg font-semibold" 
                  disabled={loading}
                >
                  {loading ? 'Sending...' : (
                    <>
                      <Send className="mr-2 h-5 w-5" />
                      Send Message
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>

      <section className="bg-gradient-to-r from-blue-600 to-indigo-700 text-white py-16">
        <div className="max-w-5xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-4">Ready to Get Started?</h2>
          <p className="text-xl text-blue-100">Join thousands of professionals who have transformed their careers with Nigerland</p>
        </div>
      </section>
    </div>
  );
};